import { REJECTION_REASONS_US } from 'consts/common';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { withLocationStateModal, DefaultModalProps } from 'utils/modals';
import { useOpenModal } from 'utils/modals/useOpenModal';
import * as yup from 'yup';

import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  JobMatchStageEnum,
  useChangeJobMatchStageMutation,
  ChangeJobMatchStageMutationVariables,
} from '@libs/graphql-types';
import Button from '@libs/ui/components/button';
import { ConnectedSelect } from '@libs/ui/components/form/select';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';

import { JOB_MATCH_STAGE_LABELS } from '../../../../consts';
import { NewActionsBlockModals } from './types';

const useStyles = makeStyles((theme) => ({
  confirmButton: {},
}));

const validator = yup.object({
  job_match_id: yup.string().required(),
  new_stage: yup.string().required(),
  message: yup.string().max(500, 'Message cannot exceed 500 characters'),
  declination_reason: yup.string().optional(),
});

type ModalDataProps = {
  jobMatchId: string;
  targetStage: JobMatchStageEnum;
};

interface ConfirmStageChangeModalProps
  extends DefaultModalProps<ModalDataProps> {}

const ConfirmStageChangeModalComponent = ({
  isOpen,
  close,
  modalData,
}: ConfirmStageChangeModalProps) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [changeStage, { loading }] = useChangeJobMatchStageMutation();
  const { jobMatchId, targetStage } = modalData || {};

  const initialValues: ChangeJobMatchStageMutationVariables = {
    job_match_id: jobMatchId || '',
    new_stage: targetStage || JobMatchStageEnum.InstantMatch,
    message: '',
    declination_reason: undefined,
  };

  const handleSubmit = async (values: ChangeJobMatchStageMutationVariables) => {
    try {
      await changeStage({
        variables: values,
      });
      enqueueSnackbar('Stage changed successfully', { variant: 'success' });
      close();
    } catch (e) {
      enqueueSnackbar('Failed to change stage', { variant: 'error' });
    }
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validator}
      enableReinitialize
      validateOnMount
    >
      {({ isValid, handleSubmit: formikHandleSubmit }) => {
        return (
          <DefaultModal
            actions={
              <Grid spacing={4} container>
                <Grid xs={6} item>
                  <Button
                    className={classes.confirmButton}
                    fullWidth
                    color="info"
                    variant="contained"
                    onClick={formikHandleSubmit}
                    disabled={loading || !isValid}
                  >
                    Ok
                  </Button>
                </Grid>
                <Grid xs={6} item>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={close}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            }
            handleClose={close}
            open={isOpen}
            title="Confirm Action"
          >
            <Box mt={3}>
              <Typography>
                Are you sure you want to move this match to{' '}
                <b>
                  {targetStage &&
                  JOB_MATCH_STAGE_LABELS[targetStage as JobMatchStageEnum]
                    ? JOB_MATCH_STAGE_LABELS[targetStage as JobMatchStageEnum]
                    : targetStage || 'Unknown'}
                </b>
                ?
              </Typography>

              <Box mt={3}>
                <ConnectedTextField
                  name="message"
                  label="Message"
                  variant="filled"
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  helperText="Optional message for stage change"
                />
              </Box>

              {targetStage === JobMatchStageEnum.Rejected && (
                <Box mt={8} style={{ textAlign: 'left' }}>
                  <ConnectedSelect
                    name="declination_reason"
                    hideNoneValue
                    options={REJECTION_REASONS_US}
                    fullWidth
                    variant="filled"
                    label="Declination Reason"
                    helperText="Optional reason for declining"
                  />
                </Box>
              )}
            </Box>
          </DefaultModal>
        );
      }}
    </Formik>
  );
};

export const ConfirmStageChangeModal = withLocationStateModal<ModalDataProps>({
  id: NewActionsBlockModals.CONFIRM_STAGE_CHANGE,
})(ConfirmStageChangeModalComponent);

export { ConfirmStageChangeModalComponent };

export const useOpenConfirmStageChangeModal = () =>
  useOpenModal<ModalDataProps>(NewActionsBlockModals.CONFIRM_STAGE_CHANGE);
